import { useProfile } from 'api/user/profile';
import { Channel, ChannelSort } from 'stream-chat';
import { useMemo, useState } from 'react';
import { getChannelArchivedKey } from '../utils';
import { ChatGenerics } from '../streamTypes';

export enum ChannelSortType {
  // Recent messages first
  RECENT_MESSAGE = 'recent_message',

  // Unread channels first
  UNREAD = 'unread',

  // Archived channels
  ARCHIVED = 'archived',
}

export interface UseChannelSortHook {
  sortBy: ChannelSortType;
  setSortBy: (sort: ChannelSortType) => void;
  /** Transformed to GetStream API sort **/
  apiSort: ChannelSort<ChatGenerics>;
}

export const useChannelSort = (): UseChannelSortHook => {
  const [sortBy, setSortBy] = useState<ChannelSortType>(ChannelSortType.RECENT_MESSAGE);
  const profile = useProfile();

  const apiSort = useMemo<ChannelSort<ChatGenerics>>(() => {
    const sortMap: Record<ChannelSortType, ChannelSort<ChatGenerics>> = {
      [ChannelSortType.RECENT_MESSAGE]: { pinned_at: -1, last_message_at: -1 },
      [ChannelSortType.UNREAD]: { pinned_at: -1, unread_count: -1 },
      [ChannelSortType.ARCHIVED]: { pinned_at: -1, [getChannelArchivedKey(profile)]: -1 },
    };
    return sortMap[sortBy];
  }, [profile, sortBy]);

  return useMemo(() => ({ sortBy, setSortBy, apiSort }), [sortBy, setSortBy, apiSort]);
};

export const sortChannels = (
  chatUserId: string,
  channels: Channel[],
  sortBy: ChannelSortType,
) => channels
  .sort((a, b) => {
    // Get pinned_at for both channels (or null if not pinned)
    const aPinnedAt = a.state.members?.[chatUserId]?.pinned_at;
    const bPinnedAt = b.state.members?.[chatUserId]?.pinned_at;

    // If both channels are pinned, sort by pin date (newest first)
    if (aPinnedAt && bPinnedAt) {
      return new Date(bPinnedAt).getTime() - new Date(aPinnedAt).getTime();
    }

    // If only one channel is pinned, it goes first
    if (aPinnedAt && !bPinnedAt) return -1;
    if (bPinnedAt && !aPinnedAt) return 1;

    if (sortBy === ChannelSortType.RECENT_MESSAGE) {
      // If neither is pinned, sort by last message date
      const aLastMessageAt = a.state.last_message_at || new Date(0).toISOString();
      const bLastMessageAt = b.state.last_message_at || new Date(0).toISOString();

      return new Date(bLastMessageAt).getTime() - new Date(aLastMessageAt).getTime();
    } else if (sortBy === ChannelSortType.UNREAD) {
      // Sort by unread count (higher first)
      const aUnreadCount = a.state.unreadCount || 0;
      const bUnreadCount = b.state.unreadCount || 0;

      return bUnreadCount - aUnreadCount;
    }
  });
